import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { TaxClassificationValues } from 'core/application/adminSignUp/common/models'
import { CompanyType } from 'core/domain/benefit/companyType'

import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import Alert from 'common/components/feedback/Alert'
import notification from 'common/components/feedback/Notification'
import CategoryItemPageHeader from 'common/components/onBoarding/Common'

import { commandRequest } from 'common/RequestUtils'
import { BaseNavigationProps } from 'common/wizard/BaseNavigationProps'

import WizardNextButton from './WizardNextButton'
import WizardPreviousButton from './WizardPreviousButton'

import './CompanyDetailsStyles.less'

const formValidator = (translate: any) =>
  Yup.object({
    companyTypeId: Yup.number().required(translate('general.required')).min(1).typeError(translate('general.required')),
  })

interface TaxClassificationProps extends BaseNavigationProps<TaxClassificationValues> {
  companyTypeId: number
  taxClassificationApi: (values: any) => Promise<void>
  descriptionMap: (translate: any) => Map<number, string>
  numberOfQuestions: number
  currentQuestion: number
}

function TaxClassification({ descriptionMap, ...props }: TaxClassificationProps) {
  const [description, setDescription] = useState<string | undefined>('')
  const [loading, setLoading] = useState<boolean>(false)
  const { t: translate } = useTranslation()

  useEffect(() => {
    if (props.companyTypeId) {
      setDescription(descriptionMap(translate).get(props.companyTypeId))
    }
  }, [props.companyTypeId])

  const onSubmit = async (values) => {
    setLoading(true)
    const res = await commandRequest(() => props.taxClassificationApi(values))
    setLoading(false)
    if (!res.errors) {
      props.goNext(props.name)
    } else {
      console.log(res.errors)
      notification.open({
        message: res.errors[0],
        type: 'error',
      })
    }
  }

  const onRadioChange = (e: any) => {
    setDescription(descriptionMap(translate).get(e.target.value))
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={props.state}
      onSubmit={onSubmit}
      validate={(values) => {
        props.hasDirtyData(true)
      }}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit} autoComplete="off" className={'t-admin-signup-tax-classification'}>
          <Row>
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 3 }} xl={{ span: 12 }}>
              <CategoryItemPageHeader
                title={translate('admin-signup.company-details.title')}
                numberOfQuestions={props.numberOfQuestions}
                currentQuestion={props.currentQuestion}
                question={translate('admin-signup.company-details.tax-classification.question-1')}
              />
            </Col>
          </Row>

          <Row className="form__row ml-12 mt-24">
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 3 }} xl={{ span: 12 }}>
              <Radio.Group
                name="companyTypeId"
                defaultValue={props.companyTypeId}
                onRadioChange={(e) => onRadioChange(e)}
                className={'w-100'}
                size={'large'}
              >
                <Row className={'mb-8'} gutter={16}>
                  <Col xs={24} md={12} className={'mb-8'}>
                    <Radio.Button className={'h-100 w-100'} value={CompanyType.CCorpNonProfitOrMunicipality}>
                      {translate('admin-signup.company-details.tax-classification.c-corp-label')}
                    </Radio.Button>
                  </Col>
                  <Col xs={24} md={12} className={'mb-8'}>
                    <Radio.Button className={'h-100 w-100'} value={CompanyType.Scorp}>
                      {translate('admin-signup.company-details.tax-classification.s-corp-label')}
                    </Radio.Button>
                  </Col>
                  <Col xs={24} md={12} className={'mb-8'}>
                    <Radio.Button value={CompanyType.Partnership}>
                      {translate('admin-signup.company-details.tax-classification.partnership-label')}
                    </Radio.Button>
                  </Col>
                  <Col xs={24} md={12} className={'mb-8'}>
                    <Radio.Button value={CompanyType.SoleProprietors}>
                      {translate('admin-signup.company-details.tax-classification.sole-proprietors-label')}
                    </Radio.Button>
                  </Col>
                  <Col xs={24} md={12} className={'mb-8'}>
                    <Radio.Button value={CompanyType.LLCFilingAsPartnership}>
                      {translate('admin-signup.company-details.tax-classification.llc-as-partnership-label')}
                    </Radio.Button>
                  </Col>
                  <Col xs={24} md={12} className={'mb-8'}>
                    <Radio.Button value={CompanyType.LLCFilingAsSCorp}>
                      {translate('admin-signup.company-details.tax-classification.llc-as-s-corp-label')}
                    </Radio.Button>
                  </Col>
                </Row>
              </Radio.Group>
            </Col>
          </Row>

          <Row className="form__row ml-12">
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 3 }} xl={{ span: 12 }}>
              {description && (
                <Alert
                  type="info"
                  message=""
                  className={'t-admin-signup-tax-classification-alert-info'}
                  description={description}
                  showIcon
                />
              )}
            </Col>
          </Row>

          <Row className="bottom__actions">
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 3 }}>
              <WizardPreviousButton
                onClick={() => {
                  props.goBack(props.name)
                }}
              />
              <WizardNextButton disabled={!formValidator(translate).isValidSync(formik.values)} loading={loading} />
            </Col>
          </Row>
        </form>
      )}
    </Formik>
  )
}

export default TaxClassification
